import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { PackageSearch, ArrowRight } from "lucide-react";
import Seo, { buildBreadcrumbLd } from "../components/Seo";
import Breadcrumbs from "../components/Breadcrumbs";
import PageHero from "../components/PageHero";
import Reveal from "../components/Reveal";
import CtaSection from "../components/CtaSection";
import BeforeAfterSlider from "../components/BeforeAfterSlider";
import { api } from "../lib/api";

const crumbs = [
  { label: "Startseite", to: "/" },
  { label: "Referenzen", to: "/referenzen" },
];

export default function Referenzen() {
  const [projects, setProjects] = useState([]);
  useEffect(() => {
    api.get("/projects/published").then((r) => setProjects(r.data)).catch(() => setProjects([]));
  }, []);

  const categories = ["Alle", ...new Set(projects.map((p) => p.object_type).filter(Boolean))];
  const [filter, setFilter] = useState("Alle");
  const visible = projects.filter((p) => filter === "Alle" || p.object_type === filter);

  return (
    <div data-testid="page-referenzen">
      <Seo
        path="/referenzen"
        title="Referenzen & Projekte | Rümpeltaxi"
        description="Ausgewählte Projekte von Rümpeltaxi. Echte Referenzen mit Vorher-Nachher-Bildern werden nach Freigabe ergänzt."
        jsonLd={buildBreadcrumbLd(crumbs)}
      />
      <Breadcrumbs items={crumbs} />
      <PageHero
        kicker="Referenzen"
        title="Ausgewählte Projekte"
        lead="Nach Abschluss und Freigabe zeigen wir hier echte Projekte mit anonymisierten Beschreibungen und Vorher-Nachher-Bildern – nach Kategorie und Region filterbar."
      />

      <section className="bg-white py-16 md:py-24">
        <div className="container-page">
          <div className="flex flex-wrap gap-2" data-testid="referenzen-filter">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setFilter(c)}
                className={`rounded-sm px-4 py-2 font-heading text-sm font-semibold transition-colors ${
                  filter === c
                    ? "bg-yellow text-brand-black"
                    : "border border-light-gray bg-light-gray text-anthracite hover:border-yellow"
                }`}
                data-testid={`filter-${c}`}
              >
                {c}
              </button>
            ))}
          </div>

          {visible.length === 0 ? (
            <Reveal className="mt-12">
              <div className="flex flex-col items-center justify-center rounded-sm border border-dashed border-mid-gray/30 bg-light-gray px-6 py-20 text-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-white">
                  <PackageSearch className="h-8 w-8 text-mid-gray" />
                </span>
                <h2 className="mt-6 font-heading text-xl font-bold text-anthracite">
                  Weitere Referenzprojekte werden nach Freigabe ergänzt.
                </h2>
                <p className="mt-3 max-w-md text-base text-mid-gray">
                  Wir zeigen ausschließlich echte, freigegebene Projekte. Bis dahin überzeugen wir
                  Sie gerne persönlich – sprechen Sie uns an.
                </p>
              </div>
            </Reveal>
          ) : (
            <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              {visible.map((p) => (
                <div key={p.id} className="overflow-hidden rounded-md border border-light-gray bg-white p-4" data-testid={`ref-project-${p.id}`}>
                  {p.before_image && p.after_image ? (
                    <BeforeAfterSlider before={p.before_image} after={p.after_image} alt={p.title} />
                  ) : p.after_image ? (
                    <img src={p.after_image} alt={p.alt_after || p.title} className="aspect-[4/3] w-full rounded-sm object-cover" loading="lazy" />
                  ) : null}
                  <div className="p-2 pt-5">
                    <h3 className="font-heading text-lg font-bold text-anthracite">{p.title}</h3>
                    <p className="text-sm text-mid-gray">{[p.object_type, p.service_type, p.region].filter(Boolean).join(" · ")}</p>
                    {p.detail_active && (
                      <Link to={`/referenzen/${p.slug}`} className="mt-3 inline-flex items-center gap-1 font-heading text-sm font-bold text-anthracite hover:text-yellow-dark" data-testid={`ref-detail-${p.slug}`}>
                        Projekt ansehen <ArrowRight className="h-4 w-4" />
                      </Link>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <CtaSection />
    </div>
  );
}
